import { call, delay, put, select, spawn, takeEvery, takeLatest } from 'redux-saga/effects';

import { auth$refresh, logout } from '@/api/auth';
import { get$user$name } from '@/api/user-cabinet';
import { AuthResponse } from '@/types/auth';
import { UserXs } from '@/types/user-cabinet';
import { storageKeys } from '@/utils/constants';
import { Cookies } from '@/utils/cookies';

import { AuthActionTypes, isLoggedInSet, userNameLoad, userNameSet } from './auth.actions';
import { selectIsLoggedIn } from './auth.selectors';

function* intervalAuthRefresh() {
    const isLoggedIn: ReturnType<typeof selectIsLoggedIn> = yield select(selectIsLoggedIn);

    while (isLoggedIn) {
        try {
            const token: AuthResponse = yield call(auth$refresh);

            yield put(userNameLoad(token.access_token));

            Cookies.set(storageKeys.accessToken, token.access_token, {
                path: '/',
                maxAge: token.expires,
                secure: true,
            });

            yield delay(token.expires - 60_000); // подождать до почти конца жизни токена
        } catch (error) {
            console.error('Token refresh loop failed:', error);
            yield put(isLoggedInSet(false));
            break;
        }
    }
}

function* initialAuthRefresh() {
    if (typeof window === 'undefined') {
        return;
    }
    const qParams = new URLSearchParams(window.location.search);
    const accessToken = qParams.get(storageKeys.accessToken);
    const refreshToken = qParams.get(storageKeys.refreshToken);
    const authReset = qParams.get(storageKeys.resetAuth);

    if (authReset === 'true') {
        yield call(logout);
        Cookies.remove(storageKeys.accessToken);
        Cookies.remove(storageKeys.refreshToken);
    }

    if (accessToken && refreshToken) {
        Cookies.set(storageKeys.accessToken, accessToken, {
            path: '/',
            sameSite: 'none',
            secure: true,
        });
        Cookies.set(storageKeys.refreshToken, refreshToken, {
            path: '/',
            sameSite: 'none',
            secure: true,
        });
    }

    try {
        const token: AuthResponse = yield call(auth$refresh);
        if (!token) return;

        Cookies.set(storageKeys.accessToken, token.access_token, {
            path: '/',
            secure: true,
            maxAge: token.expires,
        });

        yield put(isLoggedInSet(true));
    } catch {
        Cookies.remove(storageKeys.accessToken);
        Cookies.remove(storageKeys.refreshToken);
    }

    yield call(() => {
        const cleanUrl = new URL(window.location.href);
        cleanUrl.searchParams.delete(storageKeys.accessToken);
        cleanUrl.searchParams.delete(storageKeys.refreshToken);
        cleanUrl.searchParams.delete(storageKeys.resetAuth);
        window.history.replaceState({}, '', cleanUrl.toString());
    });
}

function* authRefreshSaga() {
    yield takeLatest(AuthActionTypes.isLoggedInSet, intervalAuthRefresh);
}

function* username$load(action: ReturnType<typeof userNameLoad>) {
    const user: Pick<UserXs, 'first_name'> = yield call(get$user$name, action.payload);
    yield put(userNameSet(user.first_name));
}

function* userNameLoadSaga() {
    yield takeEvery(AuthActionTypes.userNameLoad, username$load);
}

export function* authEffects() {
    yield spawn(initialAuthRefresh);
    yield spawn(authRefreshSaga);
    yield spawn(userNameLoadSaga);
}
